import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Video, Headphones } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";
import MarkdownEditor from "@/components/MarkdownEditor";
import TagsInput from "@/components/TagsInput";

const EMPTY = {
  paper_title: "", paper_authors: [], paper_year: null, paper_doi: "", paper_url: "",
  summary: "", transcript_md: "",
  video_url: "", audio_url: "", code_repo_url: "",
  duration_min: 30, format: "video",
  cover_image_url: "", tags: [], is_premium: true,
};

export default function AdminExplainersPage() {
  const [items, setItems] = useState([]);
  const pager = usePager(items, 15);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/paper-explainers"); setItems(r.data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.paper_title.trim()) { toast.error("Paper title required"); return; }
    try {
      if (editing === "new") await api.post("/admin/paper-explainers", form);
      else await api.put(`/admin/paper-explainers/${editing}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this explainer?")) return;
    await api.delete(`/admin/paper-explainers/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-explainers-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PAPER EXPLAINERS</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Manage <em className="italic text-kb-primary">explainer library</em></h1>
        </div>
        <button onClick={() => { setEditing("new"); setForm(EMPTY); }} className="kb-btn-primary text-sm" data-testid="explainer-new-btn">
          <Plus className="w-3.5 h-3.5" /> New explainer
        </button>
      </div>

      {editing && (
        <div className="mt-6 kb-card p-6" data-testid="explainer-editor">
          <div className="flex items-center justify-between mb-3">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">{editing === "new" ? "NEW EXPLAINER" : "EDITING"}</div>
            <button onClick={() => setEditing(null)} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="space-y-3">
            <input className="kb-input text-sm w-full" placeholder="Paper title *"
              value={form.paper_title} onChange={(e) => setForm({ ...form, paper_title: e.target.value })}
              data-testid="explainer-title-input" />
            <div className="grid sm:grid-cols-3 gap-2">
            <TagsInput
              value={form.paper_authors || []}
              onChange={(arr) => setForm({ ...form, paper_authors: arr })}
              placeholder="Authors (type and press Enter)"
              testId="explainer-authors"
              className="text-sm"
            />
              <input type="number" className="kb-input text-sm" placeholder="Year"
                value={form.paper_year ?? ""}
                onChange={(e) => setForm({ ...form, paper_year: e.target.value ? parseInt(e.target.value) : null })} />
              <input className="kb-input text-sm" placeholder="DOI"
                value={form.paper_doi} onChange={(e) => setForm({ ...form, paper_doi: e.target.value })} />
            </div>
            <input className="kb-input text-sm w-full" placeholder="Paper URL (arXiv, journal)"
              value={form.paper_url} onChange={(e) => setForm({ ...form, paper_url: e.target.value })} />
            <textarea rows={3} className="kb-input text-sm w-full resize-none" placeholder="Short summary"
              value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Transcript / notes (Markdown)</label>
              <MarkdownEditor value={form.transcript_md} onChange={(v) => setForm({ ...form, transcript_md: v })} />
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <select className="kb-input text-sm" value={form.format}
                onChange={(e) => setForm({ ...form, format: e.target.value })}>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
              </select>
              <input type="number" min="0" className="kb-input text-sm" placeholder="Duration (min)"
                value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || 0)) })} />
              <label className="flex items-center gap-2 text-sm text-kb-ink-muted">
                <input type="checkbox" checked={!!form.is_premium}
                  onChange={(e) => setForm({ ...form, is_premium: e.target.checked })} />
                Premium gated
              </label>
            </div>
            <input className="kb-input text-sm w-full" placeholder="Video URL (YouTube or direct .mp4)"
              value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} />
            <input className="kb-input text-sm w-full" placeholder="Audio URL (.mp3)"
              value={form.audio_url} onChange={(e) => setForm({ ...form, audio_url: e.target.value })} />
            <input className="kb-input text-sm w-full" placeholder="Code repository URL (GitHub)"
              value={form.code_repo_url} onChange={(e) => setForm({ ...form, code_repo_url: e.target.value })} />
            <input className="kb-input text-sm w-full" placeholder="Cover image URL"
              value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} />
            <TagsInput
              value={form.tags || []}
              onChange={(tags) => setForm({ ...form, tags })}
              placeholder="Tags (type and press Enter)"
              testId="explainer-tags"
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} className="kb-btn-primary text-sm" data-testid="explainer-save-btn">Save explainer</button>
            <button onClick={() => setEditing(null)} className="kb-btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        items.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No explainers yet.</div>
        ) :
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {pager.pageItems.map((e) => {
            const Icon = e.format === "audio" ? Headphones : Video;
            return (
              <div key={e.id} className="kb-card p-5" data-testid={`admin-explainer-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <Icon className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                    <h3 className="font-serif text-lg text-kb-ink mt-2 leading-snug line-clamp-2">{e.paper_title}</h3>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
                      {(e.paper_authors || []).slice(0, 3).join(", ")}{e.paper_year ? ` · ${e.paper_year}` : ""} · {e.duration_min}m
                    </div>
                    <div className="kb-mono text-[10px] uppercase tracking-wider mt-2 text-kb-ink-light">
                      {e.is_premium ? "PREMIUM" : "FREE"} · {e.format.toUpperCase()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => { setEditing(e.id); setForm({ ...EMPTY, ...e }); }} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" data-testid={`admin-explainer-${e.id}-edit`}><Edit3 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(e.id)} className="p-1.5 text-rose-600 hover:text-rose-700" data-testid={`admin-explainer-${e.id}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>}
      <Pager {...pager} label="explainers" testId="admin-explainers-pager" />
    </div>
  );
}